import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  FileSpreadsheet, Loader2, CheckCircle2, AlertTriangle, XCircle, ArrowRight, RotateCcw, Download
} from 'lucide-react';
import { keyed } from '../../lib/listKeys';
import { Link } from 'react-router-dom';

export function ResultsStep({ importing, result, duplicateAction, onStartOver, onGoToInventory, onDownloadFailures, kind = 'products', destinationLabel = 'View in Inventory' }) {
  if (importing) {
    return (
      <div className="py-20 text-center" data-testid="results-loading">
        <Loader2 className="h-6 w-6 animate-spin mx-auto text-primary mb-3" />
        <p className="text-sm text-muted-foreground">Importing your {kind}...</p>
      </div>
    );
  }
  if (!result) return null;

  return (
    <div className="space-y-4 animate-fade-in-up" data-testid="import-results">
      <div className={`grid grid-cols-2 gap-3 ${kind === 'products' && duplicateAction === 'update' ? 'sm:grid-cols-5' : kind !== 'products' && duplicateAction !== 'update' ? 'sm:grid-cols-3' : 'sm:grid-cols-4'}`}>
        <Card data-testid="result-imported">
          <CardContent className="p-4 text-center">
            <CheckCircle2 className="h-6 w-6 mx-auto mb-1 text-emerald-500" />
            <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">{result.imported}</p>
            <p className="text-xs text-muted-foreground">Imported</p>
          </CardContent>
        </Card>
        {duplicateAction === 'update' && (
          <Card data-testid="result-updated">
            <CardContent className="p-4 text-center">
              <RotateCcw className="h-6 w-6 mx-auto mb-1 text-sky-500" />
              <p className="text-2xl font-bold text-sky-600 dark:text-sky-400">{result.updated || 0}</p>
              <p className="text-xs text-muted-foreground">Updated</p>
            </CardContent>
          </Card>
        )}
        {kind === 'products' && <Card data-testid="result-stock-added">
          <CardContent className="p-4 text-center">
            <FileSpreadsheet className="h-6 w-6 mx-auto mb-1 text-primary" />
            <p className="text-2xl font-bold">{result.total_stock_added || 0}</p>
            <p className="text-xs text-muted-foreground">Stock Added</p>
          </CardContent>
        </Card>}
        <Card data-testid="result-skipped">
          <CardContent className="p-4 text-center">
            <AlertTriangle className="h-6 w-6 mx-auto mb-1 text-amber-500" />
            <p className="text-2xl font-bold text-amber-600 dark:text-amber-400">{result.skipped}</p>
            <p className="text-xs text-muted-foreground">Skipped</p>
          </CardContent>
        </Card>
        <Card data-testid="result-errors">
          <CardContent className="p-4 text-center">
            <XCircle className="h-6 w-6 mx-auto mb-1 text-rose-500" />
            <p className="text-2xl font-bold text-rose-600 dark:text-rose-400">{result.failed_count || 0}</p>
            <p className="text-xs text-muted-foreground">Failed rows</p>
          </CardContent>
        </Card>
      </div>

      {result.failed_rows?.length > 0 && (
        <div className="space-y-2" data-testid="failed-report-section">
          <Button variant="outline" className="gap-2" onClick={onDownloadFailures} data-testid="download-failed-rows">
            <Download className="h-4 w-4" /> Download failed rows ({result.failed_rows.length})
          </Button>
          <p className="text-xs text-muted-foreground" data-testid="failed-report-summary">Only failed rows are included, with original columns and error reasons. Successful rows and deliberate skips are excluded.</p>
        </div>
      )}

      {(result.suppliers_created > 0 || result.categories_created > 0 || result.manufacturers_created > 0 || result.generic_names_resolved > 0) && (
        <Card className="border-primary/30 bg-primary/5" data-testid="auto-created-banner">
          <CardContent className="p-4">
            <p className="text-sm font-medium mb-2 flex items-center gap-1.5">
              <CheckCircle2 className="h-4 w-4 text-primary" /> Auto-created from import data:
            </p>
            <div className="flex flex-wrap gap-2">
              {result.suppliers_created > 0 && (
                <Badge variant="outline" className="text-xs gap-1">{result.suppliers_created} new supplier{result.suppliers_created > 1 ? 's' : ''}</Badge>
              )}
              {result.categories_created > 0 && (
                <Badge variant="outline" className="text-xs gap-1">{result.categories_created} new categor{result.categories_created > 1 ? 'ies' : 'y'}</Badge>
              )}
              {result.manufacturers_created > 0 && (
                <Badge variant="outline" className="text-xs gap-1">{result.manufacturers_created} new manufacturer{result.manufacturers_created > 1 ? 's' : ''}</Badge>
              )}
              {result.generic_names_resolved > 0 && (
                <Badge variant="outline" className="text-xs gap-1 border-primary/40 text-primary">{result.generic_names_resolved} generic name{result.generic_names_resolved > 1 ? 's' : ''} auto-detected by AI</Badge>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {result.errors?.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-500" /> Import Issues
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="max-h-[200px] w-full min-w-0 overflow-y-auto overscroll-contain [overflow-wrap:anywhere]" data-testid="result-errors-scroll">
              <div className="space-y-1.5">
                {keyed(result.errors, err => `${err.row}-${err.error}`).map(({ item: err, key }) => (
                  <div key={key} className="flex items-start gap-2 text-sm py-1" data-testid={`result-error-${key}`}>
                    <Badge variant="outline" className="text-[10px] shrink-0">Row {err.row}</Badge>
                    <span className="text-muted-foreground break-words min-w-0 flex-1">{err.error}</span>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {[['warnings', 'Warnings'], ['skipped_rows', 'Skipped rows']].map(([field, title]) => result[field]?.length > 0 && (
        <details key={field} className="border-t pt-3" data-testid={`result-${field.replace('_', '-')}-details`}>
          <summary className="cursor-pointer text-sm font-medium" data-testid={`result-${field.replace('_', '-')}-toggle`}>{title} ({result[field].length})</summary>
          <div className="max-h-48 overflow-y-auto space-y-2 mt-3">
            {keyed(result[field], item => `${item.row}-${item.error}`).map(({ item, key }) => <p key={key} className="text-xs text-muted-foreground break-words" data-testid={`result-${field}-${key}`}>Row {item.row}: {item.error}</p>)}
          </div>
        </details>
      ))}

      <div className="flex flex-wrap justify-center gap-3">
        {kind === 'patients' && <Button asChild variant="outline"><Link to="/patient-invitations" data-testid="import-invite-patients">Invite patients</Link></Button>}
        <Button variant="outline" onClick={onStartOver} className="gap-1.5" data-testid="start-another-import-btn">
          <RotateCcw className="h-4 w-4" /> Start Another Import
        </Button>
        {(result.imported > 0 || result.updated > 0) && (
          <Button onClick={onGoToInventory} className="gap-1.5" data-testid="go-to-inventory-btn">
            {destinationLabel} <ArrowRight className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
}
